import { Button } from "@/components/ui/button";
import { Sparkles, Star, ArrowRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { StoreBadges } from "@/components/common/StoreBadges";

const heroAvatars = [
  "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=96&h=96&fit=crop&crop=faces&auto=format",
  "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=96&h=96&fit=crop&crop=faces&auto=format",
  "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=96&h=96&fit=crop&crop=faces&auto=format",
  "https://images.unsplash.com/photo-1548142813-c348350df52b?w=96&h=96&fit=crop&crop=faces&auto=format",
  "https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=96&h=96&fit=crop&crop=faces&auto=format",
];

export function Hero() {
  const { t, language } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-background noise-bg">
      {/* Layered radial gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Emerald glow from bottom-center fading up */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% 100%, hsl(var(--primary) / 0.15) 0%, transparent 70%)",
          }}
        />
        {/* Subtle warm gold glow from top-right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 85% 10%, hsl(var(--accent) / 0.06) 0%, transparent 60%)",
          }}
        />
      </div>

      {/* Fine dot-grid pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.07) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 lg:pt-36 pb-20 sm:pb-28 lg:pb-36">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
          {/* Text content -- left column */}
          <div className="text-center lg:text-left">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8 opacity-0 animate-fade-up">
              <Sparkles className="h-4 w-4" />
              <span>{t("hero.title")}</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight tracking-tight animate-rise">
              {t("hero.subtitle").split("{smarter}")[0]}
              <span className="text-gradient">{t("hero.clearer")}</span>
              {t("hero.subtitle").split("{smarter}")[1]?.split("{clearer}")[0]}
              <span className="text-gradient">{t("hero.smarter")}</span>
              {t("hero.subtitle").split("{clearer}")[1] || ""}
            </h1>

            {/* Subtext */}
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-lg mx-auto lg:mx-0 opacity-0 animate-fade-up stagger-2">
              {t("hero.description")}
            </p>

            {/* CTA buttons */}
            <div className="mt-10 flex flex-col items-center gap-4 lg:items-start opacity-0 animate-fade-up stagger-3">
              <StoreBadges location="hero" className="justify-center lg:justify-start" />
              <Button
                size="lg"
                variant="outline"
                className="h-14 px-8 text-base border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary"
                asChild
              >
                <a href="#features">{t("hero.cta")}</a>
              </Button>
            </div>

            {/* Social proof: avatars + stars */}
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 justify-center lg:justify-start opacity-0 animate-fade-up stagger-4">
              <div className="flex -space-x-2.5 shrink-0">
                {heroAvatars.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-background"
                    style={{ zIndex: heroAvatars.length - i }}
                  />
                ))}
              </div>
              <div className="text-center sm:text-left">
                <div className="flex gap-0.5 mb-0.5 justify-center sm:justify-start" aria-label="5/5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-accent fill-accent" />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("hero.lovedBy")}
                </p>
              </div>
            </div>

          </div>

          {/* Before / after transformation -- right column */}
          <div className="relative flex justify-center lg:justify-end animate-rise-scale">
            <div className="relative flex items-center">
              {/* Glow rings behind phones */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="absolute w-[340px] h-[340px] sm:w-[420px] sm:h-[420px] rounded-full bg-primary/5 animate-glow-pulse" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="absolute w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] rounded-full bg-primary/10 animate-glow-pulse" style={{ animationDelay: "0.6s" }} />
              </div>

              {/* BEFORE phone: real photo of scanning a paper receipt */}
              <div className="relative z-0 -rotate-6 translate-y-4">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium border border-border shadow-lg">
                  {t("hero.beforeStep")}
                </div>
                <div className="rounded-[1.9rem] p-2 bg-card ring-1 ring-border shadow-2xl shadow-black/40">
                  <div className="rounded-[1.5rem] overflow-hidden w-[136px] h-[294px] sm:w-[180px] sm:h-[390px] bg-background">
                    <img
                      src={language === "fr" ? "/scan-fr.png" : "/scan-en.png"}
                      alt={
                        language === "fr"
                          ? "Photo réelle d'un reçu papier numérisé avec l'application ReFound"
                          : "Real photo of a paper receipt being scanned with the ReFound app"
                      }
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>

              {/* Arrow badge between the two phones */}
              <div className="relative z-20 -mx-5 sm:-mx-6 flex flex-col items-center gap-1">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-xl shadow-primary/30 ring-4 ring-background">
                  <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <Sparkles className="w-4 h-4 text-accent animate-glow-pulse" />
              </div>

              {/* AFTER phone: enriched receipt in the app */}
              <div className="relative z-10 rotate-3 animate-float">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow-lg shadow-primary/30">
                  {t("hero.afterStep")}
                </div>
                <div className="rounded-[2.2rem] p-2.5 bg-card ring-1 ring-primary/30 shadow-2xl shadow-primary/20">
                  <div className="rounded-[1.8rem] overflow-hidden w-[170px] h-[368px] sm:w-[230px] sm:h-[498px] bg-background">
                    <img
                      src={language === "fr" ? "/mockup-fr.png" : "/mockup-en.png"}
                      alt={
                        language === "fr"
                          ? "Le même reçu enrichi dans ReFound : noms de produits clairs, images et garanties"
                          : "The same receipt enriched in ReFound: clear product names, images and warranties"
                      }
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
